import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { FileText, Shield, CheckCircle, ArrowLeft, User, Building2, Clock, Database, Lock, MapPin, Mail, Phone, Hash, Pen } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EnedisMandate = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const isOptionA = !user?.subscribed_power_kva || user.subscribed_power_kva < 36;
  const mandateType = isOptionA ? 'data_connect' : 'sge_tiers';

  const nameParts = (user?.name || '').split(' ');
  const defaultPrenom = nameParts[0] || '';
  const defaultNom = nameParts.slice(1).join(' ') || nameParts[0] || '';

  const addressParts = (user?.address || '').match(/^(.*?)(?:,\s*(\d{5})\s+(.+))?$/);

  const [clientType, setClientType] = useState('particulier');
  const [form, setForm] = useState({
    nom: defaultNom,
    prenom: defaultPrenom,
    adresse: addressParts ? (addressParts[1] || user?.address || '') : (user?.address || ''),
    code_postal: addressParts?.[2] || '',
    ville: addressParts?.[3] || '',
    email: user?.email || '',
    telephone: user?.phone || '',
    prm: user?.pdl_linky || '',
    raison_sociale: '',
    siret: '',
    lieu_signature: '',
  });
  const [consents, setConsents] = useState({ c1: false, c2: false, c3: false });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const allConsentsChecked = consents.c1 && consents.c2 && consents.c3;
  const isFormValid = form.nom && form.prenom && form.adresse && form.code_postal && form.ville && form.email && form.prm && form.lieu_signature && allConsentsChecked && (clientType === 'particulier' || (form.raison_sociale && form.siret));

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!isFormValid) {
      toast.error('Veuillez remplir tous les champs obligatoires et cocher les consentements');
      return;
    }
    if (form.prm.replace(/\s/g, '').length !== 14) {
      toast.error('Le numéro PRM doit contenir exactement 14 chiffres');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/enedis/mandate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          mandate_type: mandateType,
          client_type: clientType,
          ...form,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(true);
        toast.success('Mandat signé avec succès !');
      } else {
        toast.error(data.detail || 'Erreur lors de la signature du mandat');
      }
    } catch {
      toast.error('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setConsents({ c1: false, c2: false, c3: false });
    setForm(prev => ({ ...prev, lieu_signature: '' }));
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center p-6" data-testid="mandate-success-screen">
        <div className="w-full max-w-lg text-center">
          <div className="w-20 h-20 rounded-full bg-green-100 border-2 border-green-200 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#052e16] mb-3 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Mandat signé avec succès
          </h1>
          <Card className="border border-green-100/60 shadow-sm mt-6">
            <CardContent className="p-6 sm:p-8">
              <p className="text-[#64748b] leading-relaxed mb-4">
                Votre autorisation d'accès aux données Enedis a bien été enregistrée.
              </p>
              <p className="text-[#64748b] text-sm mb-4">
                Un email de confirmation vous a été envoyé à <strong className="text-[#052e16]">{form.email}</strong>.
              </p>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-left">
                <p className="text-green-800 text-sm font-medium mb-2">Récapitulatif</p>
                <p className="text-green-700 text-sm">
                  Type : <strong>{isOptionA ? 'Data Connect (particulier)' : 'SGE Tiers (professionnel)'}</strong><br />
                  PRM : <strong style={{ fontFamily: 'JetBrains Mono, monospace' }}>{form.prm}</strong><br />
                  Durée : <strong>Indéterminée (révocable à tout moment)</strong>
                </p>
              </div>
              <p className="text-[#64748b] text-xs mt-4">
                Vous pouvez révoquer ce mandat à tout moment en contactant <strong>contacte@greenteva-energies.fr</strong>.
              </p>
            </CardContent>
          </Card>
          <Button
            onClick={() => navigate('/dashboard')}
            className="mt-6 bg-green-600 hover:bg-green-700 text-white rounded-full px-8 py-2.5 font-semibold shadow-lg shadow-green-600/20"
            data-testid="mandate-go-dashboard"
          >
            Accéder à mon tableau de bord
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="enedis-mandate-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-800 text-white">
        <div className="max-w-3xl mx-auto px-6 py-8 sm:py-10">
          <div className="flex items-center gap-3 mb-4">
            <Link to="/dashboard" className="text-white/80 hover:text-white transition-colors" data-testid="mandate-back-link">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <img src="/logo.png" alt="Green Teva" className="w-9 h-9 rounded-xl" />
            <span className="text-lg font-bold">Green Teva Énergie</span>
          </div>
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/15 rounded-full text-xs font-medium">
              <FileText className="w-3.5 h-3.5" /> Document légal
            </span>
            {isOptionA ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-500/20 rounded-full text-xs font-medium">
                <Database className="w-3.5 h-3.5" /> Option A — Data Connect
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-500/20 rounded-full text-xs font-medium">
                <Shield className="w-3.5 h-3.5" /> Option B — SGE Tiers
              </span>
            )}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Mandat d'accès aux données de comptage
          </h1>
          <p className="text-green-100 text-sm mt-2">
            Données Linky via le {isOptionA ? 'Data Connect' : 'SGE'} Enedis — Art. L.341-4 et R.341-13 du Code de l'énergie
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {/* Section 1: Type de compte */}
        <Card className="border border-green-100 shadow-sm rounded-2xl overflow-hidden" data-testid="mandate-section-type">
          <CardContent className="p-6">
            <SectionTitle number="1" title="Type de compte" />
            <div className="grid grid-cols-2 gap-3 mt-4">
              <button
                type="button"
                onClick={() => setClientType('particulier')}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${clientType === 'particulier' ? 'border-green-500 bg-green-50 text-green-700' : 'border-slate-200 text-[#64748b] hover:border-green-200'}`}
                data-testid="mandate-type-particulier"
              >
                <User className="w-5 h-5" />
                <span className="font-medium text-sm">Particulier</span>
              </button>
              <button
                type="button"
                onClick={() => setClientType('professionnel')}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${clientType === 'professionnel' ? 'border-green-500 bg-green-50 text-green-700' : 'border-slate-200 text-[#64748b] hover:border-green-200'}`}
                data-testid="mandate-type-professionnel"
              >
                <Building2 className="w-5 h-5" />
                <span className="font-medium text-sm">Professionnel</span>
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Coordonnées */}
        <Card className="border border-green-100 shadow-sm rounded-2xl overflow-hidden" data-testid="mandate-section-identity">
          <CardContent className="p-6">
            <SectionTitle number="2" title="Vos coordonnées (Mandant)" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <FormField label="Nom *" icon={User} value={form.nom} onChange={v => handleChange('nom', v)} placeholder="Dupont" testId="mandate-nom" />
              <FormField label="Prénom *" icon={User} value={form.prenom} onChange={v => handleChange('prenom', v)} placeholder="Marie" testId="mandate-prenom" />

              {clientType === 'professionnel' && (
                <>
                  <div className="sm:col-span-2">
                    <FormField label="Raison sociale *" icon={Building2} value={form.raison_sociale} onChange={v => handleChange('raison_sociale', v)} placeholder="SARL Dupont Énergie" testId="mandate-raison" />
                  </div>
                  <div className="sm:col-span-2">
                    <FormField label="Numéro SIRET *" icon={Hash} value={form.siret} onChange={v => handleChange('siret', v)} placeholder="12345678901234" maxLength={14} testId="mandate-siret" />
                  </div>
                </>
              )}

              <div className="sm:col-span-2">
                <FormField label="Adresse complète *" icon={MapPin} value={form.adresse} onChange={v => handleChange('adresse', v)} placeholder="12 rue de la Paix" testId="mandate-adresse" />
              </div>
              <FormField label="Code postal *" value={form.code_postal} onChange={v => handleChange('code_postal', v)} placeholder="75001" maxLength={5} testId="mandate-cp" />
              <FormField label="Ville *" value={form.ville} onChange={v => handleChange('ville', v)} placeholder="Paris" testId="mandate-ville" />
              <FormField label="Email *" icon={Mail} value={form.email} onChange={v => handleChange('email', v)} placeholder="marie.dupont@email.fr" type="email" testId="mandate-email" />
              <FormField label="Téléphone" icon={Phone} value={form.telephone} onChange={v => handleChange('telephone', v)} placeholder="06 01 02 03 04" testId="mandate-tel" />
              <div className="sm:col-span-2">
                <FormField label="Numéro de PRM (Point de Référence de Mesure) *" icon={Hash} value={form.prm} onChange={v => handleChange('prm', v)} placeholder="12345678901234" maxLength={14} testId="mandate-prm" />
                <p className="text-[11px] text-[#64748b] mt-1">14 chiffres — visible sur votre facture d'électricité ou votre espace Enedis</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 3: Données collectées */}
        <Card className="border border-green-100 shadow-sm rounded-2xl overflow-hidden" data-testid="mandate-section-data">
          <CardContent className="p-6">
            <SectionTitle number="3" title="Données auxquelles vous autorisez l'accès" />
            <p className="text-sm text-[#64748b] mt-3 mb-4">
              En signant ce mandat, vous autorisez <strong className="text-[#052e16]">Green Teva Énergie</strong> à accéder aux catégories de données suivantes auprès d'Enedis :
            </p>
            <div className="rounded-xl border border-green-100 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-green-50">
                    <th className="text-left px-4 py-3 font-semibold text-[#052e16] text-xs uppercase tracking-widest">Catégorie</th>
                    <th className="text-left px-4 py-3 font-semibold text-[#052e16] text-xs uppercase tracking-widest">Détail</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-green-50">
                  <tr><td className="px-4 py-3 font-medium text-[#052e16]">Données techniques du PDL</td><td className="px-4 py-3 text-[#64748b]">PRM, puissance souscrite, option tarifaire (Base, HP/HC...), tension</td></tr>
                  <tr><td className="px-4 py-3 font-medium text-[#052e16]">Courbe de charge (CDC)</td><td className="px-4 py-3 text-[#64748b]">Consommation toutes les 30 min — jusqu'à 24 mois d'historique</td></tr>
                  <tr><td className="px-4 py-3 font-medium text-[#052e16]">Index de consommation</td><td className="px-4 py-3 text-[#64748b]">Relevés quotidiens et mensuels par option tarifaire</td></tr>
                  <tr><td className="px-4 py-3 font-medium text-[#052e16]">Données de production</td><td className="px-4 py-3 text-[#64748b]">Injection réseau, autoconsommation et surplus (si applicable)</td></tr>
                  <tr><td className="px-4 py-3 font-medium text-[#052e16]">Données contractuelles</td><td className="px-4 py-3 text-[#64748b]">Statut contrat, fournisseur actuel, date de prise en charge</td></tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Section 4: Durée */}
        <Card className="border border-green-100 shadow-sm rounded-2xl overflow-hidden" data-testid="mandate-section-duration">
          <CardContent className="p-6">
            <SectionTitle number="4" title="Durée du mandat" />
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="radio" name="duree" value="permanente" defaultChecked className="w-4 h-4 text-green-600 border-green-300 focus:ring-green-500" data-testid="mandate-duree-permanente" />
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-[#052e16]">Durée indéterminée (révocable à tout moment)</span>
                </div>
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Section 5: RGPD */}
        <Card className="border border-green-100 shadow-sm rounded-2xl overflow-hidden" data-testid="mandate-section-rgpd">
          <CardContent className="p-6">
            <SectionTitle number="5" title="Vos droits (RGPD)" />
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800 leading-relaxed">
                  Conformément au <strong>Règlement (UE) 2016/679 (RGPD)</strong>, vous disposez d'un droit d'accès, de rectification, d'effacement, de portabilité et d'opposition sur vos données. Vous pouvez exercer ces droits ou révoquer ce mandat à tout moment en contactant <strong>contacte@greenteva-energies.fr</strong>. En cas de litige, vous pouvez saisir la CNIL (www.cnil.fr). Les données collectées ne sont utilisées qu'aux fins définies dans ce mandat et ne sont jamais revendues à des tiers.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 6: Consentements */}
        <Card className="border border-green-100 shadow-sm rounded-2xl overflow-hidden" data-testid="mandate-section-consent">
          <CardContent className="p-6">
            <SectionTitle number="6" title="Consentement" />
            <p className="text-sm text-[#64748b] mt-3 mb-4">Veuillez cocher les trois cases pour valider votre mandat :</p>
            <div className="space-y-3">
              <ConsentCheckbox
                id="c1"
                checked={consents.c1}
                onChange={v => setConsents(p => ({ ...p, c1: v }))}
                label="J'ai lu et j'accepte les conditions du présent mandat d'accès aux données de comptage."
                testId="mandate-consent-c1"
              />
              <ConsentCheckbox
                id="c2"
                checked={consents.c2}
                onChange={v => setConsents(p => ({ ...p, c2: v }))}
                label={<>J'autorise <strong>Green Teva Énergie</strong> à accéder aux données de mon compteur communicant (Linky) auprès d'Enedis, pour les finalités décrites ci-dessus.</>}
                testId="mandate-consent-c2"
              />
              <ConsentCheckbox
                id="c3"
                checked={consents.c3}
                onChange={v => setConsents(p => ({ ...p, c3: v }))}
                label="J'ai été informé(e) de mes droits RGPD et je comprends que je peux révoquer ce mandat à tout moment."
                testId="mandate-consent-c3"
              />
            </div>
          </CardContent>
        </Card>

        {/* Section 7: Signature */}
        <Card className="border border-green-100 shadow-sm rounded-2xl overflow-hidden" data-testid="mandate-section-signature">
          <CardContent className="p-6">
            <SectionTitle number="7" title="Signature" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-[#052e16]">Date *</Label>
                <Input
                  type="date"
                  defaultValue={new Date().toISOString().split('T')[0]}
                  className="h-11 bg-white border-green-200 rounded-xl text-sm focus:border-green-500 focus:ring-4 focus:ring-green-500/10"
                  data-testid="mandate-date"
                  readOnly
                />
              </div>
              <FormField
                label="Fait à *"
                icon={MapPin}
                value={form.lieu_signature}
                onChange={v => handleChange('lieu_signature', v)}
                placeholder="Paris"
                testId="mandate-lieu"
              />
            </div>

            {/* Signature pad zone */}
            <div className="mt-6 p-4 border-2 border-dashed border-green-200 rounded-xl bg-green-50/30">
              <div className="flex items-center gap-2 mb-2">
                <Pen className="w-4 h-4 text-green-600" />
                <span className="text-xs font-semibold text-[#052e16] uppercase tracking-widest">Signature électronique</span>
              </div>
              <p className="text-xs text-[#64748b] mb-3">
                En cliquant sur "Signer et valider le mandat", vous certifiez l'exactitude des informations fournies et donnez votre accord conformément aux conditions ci-dessus.
              </p>
              <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-green-100">
                <Lock className="w-4 h-4 text-green-600 shrink-0" />
                <p className="text-xs text-[#64748b]">
                  Signature sécurisée — {form.prenom} {form.nom} — {new Date().toLocaleDateString('fr-FR')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2 pb-8">
          <Button
            variant="outline"
            onClick={handleReset}
            className="border-2 border-green-100 text-green-700 hover:border-green-600 hover:bg-green-50 rounded-full px-6 py-2.5 font-medium"
            data-testid="mandate-reset-btn"
          >
            Réinitialiser
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid || loading}
            className="bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-600/20 rounded-full px-8 py-2.5 font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            data-testid="mandate-submit-btn"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Signer et valider le mandat
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ---- Sub-components ---- */

const SectionTitle = ({ number, title }) => (
  <div className="flex items-center gap-3">
    <span className="w-7 h-7 rounded-lg bg-green-600 text-white flex items-center justify-center text-xs font-bold">{number}</span>
    <h3 className="text-sm font-bold text-[#052e16] uppercase tracking-widest" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h3>
  </div>
);

const FormField = ({ label, icon: Icon, value, onChange, placeholder, type = 'text', maxLength, testId }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-medium text-[#052e16]">{label}</Label>
    <div className="relative">
      {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />}
      <Input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className={`h-11 bg-white border-green-200 rounded-xl text-sm focus:border-green-500 focus:ring-4 focus:ring-green-500/10 ${Icon ? 'pl-10' : ''}`}
        data-testid={testId}
      />
    </div>
  </div>
);

const ConsentCheckbox = ({ id, checked, onChange, label, testId }) => (
  <label className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200 hover:border-green-200 transition-colors cursor-pointer">
    <input
      type="checkbox"
      id={id}
      checked={checked}
      onChange={e => onChange(e.target.checked)}
      className="rounded border-green-300 text-green-600 focus:ring-green-500 mt-0.5 shrink-0"
      data-testid={testId}
    />
    <span className="text-sm text-[#3f6212] leading-relaxed">{label}</span>
  </label>
);

export default EnedisMandate;
